import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Save, Plus, Trash2, Calendar, Clock, Ban, Settings2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { key: "mon", label: "Lundi" },
  { key: "tue", label: "Mardi" },
  { key: "wed", label: "Mercredi" },
  { key: "thu", label: "Jeudi" },
  { key: "fri", label: "Vendredi" },
  { key: "sat", label: "Samedi" },
  { key: "sun", label: "Dimanche" },
];

export default function BookingsConfigAdmin() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newClosed, setNewClosed] = useState("");

  const load = async () => {
    const { data } = await api.get("/settings");
    setS({
      ...data,
      booking_hours: data.booking_hours || {},
      booking_closed_days: data.booking_closed_days || [],
    });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Configuration enregistrée");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const addRange = (dayKey) => {
    const hours = { ...(s.booking_hours || {}) };
    hours[dayKey] = [...(hours[dayKey] || []), { open: "09:00", close: "18:00" }];
    setS({ ...s, booking_hours: hours });
  };
  const updateRange = (dayKey, idx, field, val) => {
    const hours = { ...(s.booking_hours || {}) };
    hours[dayKey] = [...(hours[dayKey] || [])];
    hours[dayKey][idx] = { ...hours[dayKey][idx], [field]: val };
    setS({ ...s, booking_hours: hours });
  };
  const removeRange = (dayKey, idx) => {
    const hours = { ...(s.booking_hours || {}) };
    hours[dayKey] = (hours[dayKey] || []).filter((_, i) => i !== idx);
    setS({ ...s, booking_hours: hours });
  };
  const addClosed = () => {
    if (!newClosed) return;
    const list = [...(s.booking_closed_days || [])];
    if (!list.includes(newClosed)) list.push(newClosed);
    list.sort();
    setS({ ...s, booking_closed_days: list });
    setNewClosed("");
  };
  const removeClosed = (d) => {
    setS({ ...s, booking_closed_days: (s.booking_closed_days || []).filter(x => x !== d) });
  };

  if (!s) return <div className="p-10 text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div>;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="bookings-config-admin">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Réservation</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Configuration</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Horaires de réservation, délai minimum, fenêtre maximale et jours fermés.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)]" data-testid="save-booking-config">
          <Save className="w-4 h-4 mr-1.5" />{saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </header>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Settings2 className="w-4 h-4" />Activation</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">La réservation en ligne est un module indépendant de la boutique.</div>
          </div>
          <Switch checked={!!s.booking_enabled} onCheckedChange={v => setS({ ...s, booking_enabled: v })} data-testid="booking-enabled-toggle" />
        </div>

        <div className="grid md:grid-cols-4 gap-3 pt-3 border-t">
          <div>
            <Label>Délai minimum (h)</Label>
            <Input type="number" min={0} value={s.booking_lead_hours ?? 2} onChange={e => setS({ ...s, booking_lead_hours: +e.target.value })} data-testid="booking-lead-hours" />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Heures d'avance avant un RDV</div>
          </div>
          <div>
            <Label>Fenêtre max (jours)</Label>
            <Input type="number" min={1} value={s.booking_max_days_ahead ?? 60} onChange={e => setS({ ...s, booking_max_days_ahead: +e.target.value })} data-testid="booking-max-days" />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Au-delà, aucun créneau</div>
          </div>
          <div>
            <Label>Pas des créneaux (min)</Label>
            <Input type="number" min={5} step={5} value={s.booking_slot_granularity_min ?? 15} onChange={e => setS({ ...s, booking_slot_granularity_min: +e.target.value })} />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Ex : 15 → créneaux toutes les 15 min</div>
          </div>
          <div>
            <Label>Capacité par défaut</Label>
            <Input type="number" min={1} value={s.booking_default_capacity ?? 1} onChange={e => setS({ ...s, booking_default_capacity: +e.target.value })} />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">RDV simultanés si aucun limite par service</div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Switch checked={!!s.booking_require_phone} onCheckedChange={v => setS({ ...s, booking_require_phone: v })} id="rphone" />
          <Label htmlFor="rphone" className="text-sm">Téléphone obligatoire pour réserver</Label>
        </div>

        <div>
          <Label>Texte d'accueil page réservation</Label>
          <Textarea rows={2} value={s.booking_intro_text || ""} onChange={e => setS({ ...s, booking_intro_text: e.target.value })} placeholder="Ex : Réservez votre intervention en moins d'une minute…" />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Clock className="w-4 h-4" />Horaires d'ouverture à la réservation</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">Définissez les plages où des créneaux peuvent être proposés. Un jour sans plage = fermé.</div>

        <div className="space-y-3">
          {DAYS.map(d => {
            const ranges = s.booking_hours?.[d.key] || [];
            return (
              <div key={d.key} className="flex items-start gap-3 py-2 border-b last:border-0" data-testid={`booking-day-${d.key}`}>
                <div className="w-24 shrink-0 pt-2 font-semibold text-sm">{d.label}</div>
                <div className="flex-1 space-y-2">
                  {ranges.length === 0 && (
                    <div className="text-xs text-[hsl(var(--muted-foreground))] italic py-2">Fermé</div>
                  )}
                  {ranges.map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input type="time" value={r.open} onChange={e => updateRange(d.key, i, "open", e.target.value)} className="w-32" />
                      <span className="text-sm">→</span>
                      <Input type="time" value={r.close} onChange={e => updateRange(d.key, i, "close", e.target.value)} className="w-32" />
                      <Button variant="ghost" size="sm" onClick={() => removeRange(d.key, i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => addRange(d.key)} data-testid={`add-range-${d.key}`}>
                    <Plus className="w-3 h-3 mr-1" />Ajouter une plage
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]"><Ban className="w-4 h-4" />Jours fermés exceptionnels</div>
        <div className="flex items-center gap-2">
          <Input type="date" value={newClosed} onChange={e => setNewClosed(e.target.value)} className="w-48" data-testid="closed-date-input" />
          <Button onClick={addClosed} variant="outline" data-testid="add-closed-date">
            <Plus className="w-3.5 h-3.5 mr-1" />Ajouter
          </Button>
        </div>
        {(s.booking_closed_days || []).length === 0 ? (
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Aucun jour exceptionnel</div>
        ) : (
          <div className="flex flex-wrap gap-2 pt-2">
            {(s.booking_closed_days || []).map(d => (
              <button key={d} onClick={() => removeClosed(d)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-700 text-xs border border-red-200 hover:bg-red-100">
                <Calendar className="w-3 h-3" />{d} <Trash2 className="w-3 h-3 ml-1" />
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
